import type { BookingRow } from './booking';
import type { FinancePeriod } from './financeAnalytics';

/**
 * DATEV EXTF ("Buchungsstapel") — the format the tax advisor can import directly, instead of
 * re-typing a CSV. Built from the SAME BookingRow list as the CSV, so the two files can never
 * disagree about a booking.
 *
 * Structure: a header line describing the stapel, a line of column names, then one line per
 * booking. Fields are semicolon-separated; text is quoted, numbers use a German decimal comma,
 * dates are DDMM (the year comes from the Wirtschaftsjahr in the header).
 *
 * Scope note, deliberately: only the leading fields DATEV needs for a Debitoren-/Kreditorenbuchung
 * are filled. The format has well over a hundred optional columns (currency conversion, Zahlweise,
 * EU-Steuersätze, Anlagenbuchführung …) which do not apply to this stapel and are left empty
 * rather than filled with plausible-looking values.
 */
export interface ExtfOptions {
  /** Beraternummer from the tax advisor (DATEV mandate). */
  readonly consultantNumber: string;
  /** Mandantennummer from the tax advisor. */
  readonly clientNumber: string;
  /** First day of the Wirtschaftsjahr, as YYYY-MM-DD. */
  readonly fiscalYearStart: string;
  /** Length of the Sachkonten — Personenkonten are one digit longer. */
  readonly accountLength: number;
  readonly period: FinancePeriod;
  readonly description: string;
  /** ISO timestamp used for the "created at" stamp in the header. */
  readonly createdAt: string;
  readonly createdBy: string;
}

function quote(value: string): string {
  return `"${value.replace(/"/g, '""')}"`;
}

function amount(value: number): string {
  return value.toFixed(2).replace('.', ',');
}

/** DATEV wants DDMM for a booking date; the year comes from the Wirtschaftsjahr. */
function dayMonth(isoDate: string): string {
  const [, month, day] = isoDate.split('-');
  return `${day}${month}`;
}

function compactDate(isoDate: string): string {
  return isoDate.replace(/-/g, '');
}

/** DATEV header stamp: YYYYMMDDHHMMSSmmm. */
function stamp(isoTimestamp: string): string {
  return isoTimestamp.replace(/[-:TZ.]/g, '').slice(0, 17).padEnd(17, '0');
}

/**
 * The 25-field EXTF header. Field order is fixed by the format; the ones we cannot fill honestly
 * (Sachkontenrahmen, Festschreibung) are left empty so DATEV applies its own defaults instead of
 * receiving a guess.
 */
export function extfHeader(options: ExtfOptions): string {
  const fiscalStart = compactDate(options.fiscalYearStart);
  const from = options.period.from ? compactDate(options.period.from) : fiscalStart;
  const to = options.period.to ? compactDate(options.period.to) : fiscalStart;
  return [
    quote('EXTF'), // Kennzeichen
    '700', // Versionsnummer
    '21', // Format-Kategorie: Buchungsstapel
    quote('Buchungsstapel'),
    '13', // Formatversion
    stamp(options.createdAt),
    '', // Importiert (leer)
    quote('FORQ'), // Herkunft
    quote(options.createdBy), // Exportiert von
    quote(''), // Importiert von
    options.consultantNumber,
    options.clientNumber,
    fiscalStart,
    String(options.accountLength),
    from,
    to,
    quote(options.description.slice(0, 30)),
    quote(''), // Diktatkürzel
    '1', // Buchungstyp: 1 = Finanzbuchführung
    '0', // Rechnungslegungszweck
    '0', // Festschreibung: 0 = nicht festgeschrieben
    quote('EUR'),
    '', // Derivatskurs
    '', // Reserviert
    '', // Reserviert
    quote(''), // Reserviert
    '', // Reserviert
    quote(''), // Reserviert
    quote(''), // Reserviert
    quote(''), // Anwendungsinformation
  ].join(';');
}

const columns: readonly string[] = [
  'Umsatz (ohne Soll/Haben-Kz)', 'Soll/Haben-Kennzeichen', 'WKZ Umsatz', 'Kurs', 'Basis-Umsatz',
  'WKZ Basis-Umsatz', 'Konto', 'Gegenkonto (ohne BU-Schlüssel)', 'BU-Schlüssel', 'Belegdatum',
  'Belegfeld 1', 'Belegfeld 2', 'Skonto', 'Buchungstext', 'Postensperre',
  'Diverse Adressnummer', 'Geschäftspartnerbank', 'Sachverhalt', 'Zinssperre', 'Beleglink',
  'KOST1 - Kostenstelle', 'KOST2 - Kostenstelle', 'KOST-Menge',
];

export function extfColumnHeader(): string {
  return columns.map(quote).join(';');
}

/** One EXTF booking line. */
export function extfRow(row: BookingRow): string {
  return [
    amount(row.umsatz), // Umsatz ist IMMER positiv
    quote(row.sollHaben),
    quote('EUR'),
    '', // Kurs
    '', // Basis-Umsatz
    quote(''), // WKZ Basis-Umsatz
    row.konto,
    row.gegenkonto,
    row.buSchluessel,
    dayMonth(row.belegdatum),
    quote(row.belegfeld1),
    quote(''), // Belegfeld 2
    '', // Skonto
    quote(row.buchungstext),
    '', // Postensperre
    quote(''), // Diverse Adressnummer
    quote(''), // Geschäftspartnerbank
    quote(''), // Sachverhalt
    '', // Zinssperre
    quote(''), // Beleglink
    quote(row.kost1),
    quote(''), // KOST2
    '', // KOST-Menge
  ].join(';');
}

export function buildExtf(rows: readonly BookingRow[], options: ExtfOptions): string {
  return [extfHeader(options), extfColumnHeader(), ...rows.map(extfRow)].join('\r\n');
}

export function extfFileName(tenantShortName: string, period: FinancePeriod): string {
  const suffix = period.from && period.to
    ? `${compactDate(period.from)}-${compactDate(period.to)}`
    : 'gesamt';
  return `EXTF_Buchungsstapel_${tenantShortName}_${suffix}.csv`;
}

/**
 * Triggers the download. DATEV expects Windows-1252; the browser cannot encode that directly, so
 * the file is written as UTF-8 with a BOM — DATEV reads it, and the umlauts survive. Naming this
 * out loud because a silent encoding assumption is exactly what corrupts a Buchungstext.
 */
export function downloadExtf(content: string, fileName: string): void {
  const blob = new Blob([`﻿${content}`], { type: 'text/csv;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = fileName;
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  URL.revokeObjectURL(url);
}
